'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { IconApple } from '@/components/icons/apple';
import { IconGooglePlay } from '@/components/icons/google-play';
import { Logo } from '@/components/layout/logo';
import { StoreBadge } from '@/components/ui/store-badge';
import {
  appStoreUrl,
  CONTACT_EMAIL,
  SHOW_ANDROID,
  STORE_LINKS,
} from '@/lib/constants';

const AUTH_ROUTES = ['/login', '/signup'];

const PRODUCT_LINKS = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'Support', href: '/support' },
] as const;

const COMPANY_LINKS = [
  { label: 'Contact', href: `mailto:${CONTACT_EMAIL}` },
  { label: 'Privacy', href: '/legal/privacy' },
  { label: 'Terms', href: '/legal/terms' },
] as const;

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: readonly { label: string; href: string }[];
}) {
  return (
    <div>
      <span className="text-text-tertiary mb-5 block text-[11px] font-semibold tracking-[1.4px] uppercase">
        {title}
      </span>
      <div className="flex flex-col gap-[18px]">
        {links.map((link) => (
          <Link
            key={link.label}
            href={link.href}
            className="text-foreground text-sm font-medium no-underline transition-opacity hover:opacity-70"
          >
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Footer() {
  const pathname = usePathname();
  if (AUTH_ROUTES.includes(pathname)) return null;

  return (
    <footer className="container pb-8">
      <div className="border-foreground/10 border-t pt-16">
        <div className="grid gap-12 pb-24 md:pb-28 lg:grid-cols-[440px_1fr]">
          {/* Brand */}
          <div>
            <Logo className="[&>span]:text-[32px]" />
            <p className="font-handwritten text-text-tertiary mt-2 text-[19px]">
              capture moments together
            </p>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-3">
            <FooterColumn title="Product" links={PRODUCT_LINKS} />
            <FooterColumn title="Company" links={COMPANY_LINKS} />
            <div className="col-span-2 md:col-span-1">
              <span className="text-text-tertiary mb-5 block text-[11px] font-semibold tracking-[1.4px] uppercase">
                Get the app
              </span>
              <div className="flex flex-wrap items-start gap-3 md:flex-col">
                <StoreBadge
                  href={appStoreUrl('web_footer')}
                  ariaLabel={STORE_LINKS.appStore.label}
                  icon={<IconApple className="text-background size-[22px]" />}
                  topLine="Download on the"
                  storeName="App Store"
                  external
                />
                {SHOW_ANDROID && (
                  <StoreBadge
                    href={STORE_LINKS.googlePlay.href}
                    ariaLabel={STORE_LINKS.googlePlay.label}
                    icon={
                      <IconGooglePlay className="text-background size-[22px]" />
                    }
                    topLine="Get it on"
                    storeName="Google Play"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-foreground/10 flex flex-wrap items-center justify-between gap-3 border-t pt-7">
          <span className="text-text-tertiary text-[13px]">
            &copy; 2026 Keeps
          </span>
          <span className="text-text-tertiary text-[13px]">
            <Link
              href="/legal/privacy"
              className="hover:text-foreground no-underline transition-colors"
            >
              Privacy
            </Link>
            {' · '}
            <Link
              href="/legal/terms"
              className="hover:text-foreground no-underline transition-colors"
            >
              Terms
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
